import { CarouselSection } from '@shipt/design-system-carousel'
import { spacingPx } from '@/features/shared/theme/tokens'
import styled from 'styled-components'
import chunk from 'lodash/chunk'
import {
  type Image as ImageType,
  type Link,
  type Disclaimer,
} from '@/features/cms/components/CMS/types'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import {
  trackAvailableStoreClicked,
  trackAvailableStoreViewed,
} from '@/features/shared/analytics/selectStore'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { StoreTile } from '@/features/cms/components/CMS/components/BrandAmplification/StoreTile'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { TransparentAnchor } from '@/features/shared/elements/Buttons'
import { useHeaderRect } from '@/features/shared/hooks/useHeaderRect'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { logWarn } from '@/features/shared/utils/logger'
import { type StoreCardsRenderData } from '@/features/cms/components/CMS/components/StoresCarousel'
import { useQueryAllShoppingCarts } from '@/features/shop/services/Carts/queries'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'
import { Skeleton } from '@shipt/design-system-loading'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { BotOnly } from '@/features/shared/components/BotOnly'
import { getRetailerUrl } from '@shared/constants/routes'
import { type ReactNode } from 'react'
import { theme } from '@/features/shared/constants/shiptTheme'

export type StoresCarouselProps = {
  heading?: string
  deliveryStoresList: StoreCardsRenderData[]
  view_all_link?: Link
  content_type_id: string
  id: string
  analyticSlug?: string
  seoHeadLevel: number
  image?: ImageType
  mobile_image?: ImageType
  subheading?: string
  background_color_hex?: string
  disclaimer?: Disclaimer
  carousel_title?: string
}

/**
 * Props passed to a variant's header components. The base injects the shared
 * `handleViewAll` behavior; everything else is forwarded from the caller.
 */
export type StoresCarouselHeaderProps = {
  heading?: string
  subheading?: string
  seoHeadLevel: number
  view_all_link?: Link
  handleViewAll: () => void
  image?: ImageType
  mobile_image?: ImageType
  disclaimer?: Disclaimer
  carousel_title?: string
}

type StoresCarouselWrapper = React.ComponentType<{
  id: string
  background_color_hex?: string
  'data-has-bg': boolean
  children: ReactNode
}>

type StoresCarouselConfig = {
  /** Variant-specific styled wrapper (owns the layout/background CSS). */
  Wrapper: StoresCarouselWrapper
  DesktopHeader: React.ComponentType<StoresCarouselHeaderProps>
  MobileHeader: React.ComponentType<StoresCarouselHeaderProps>
  /** When true, wraps the desktop header + carousel in a `CarouselSection.Card`. */
  wrapDesktopInCard?: boolean
}

export const createStoresCarousel = ({
  Wrapper,
  DesktopHeader,
  MobileHeader,
  wrapDesktopInCard = false,
}: StoresCarouselConfig) => {
  const StoresCarousel = ({
    heading,
    deliveryStoresList,
    view_all_link,
    content_type_id,
    id,
    analyticSlug,
    seoHeadLevel = 2,
    mobile_image,
    image,
    subheading,
    disclaimer,
    background_color_hex,
    carousel_title,
  }: StoresCarouselProps) => {
    const { selectStore } = useGlobalHomeSelectStore()
    const { screenSm: isMobile } = useMediaQuery()

    const { active: smallerTilesOnMobile } = useExperiment(
      ExperimentType.SNY_SMALLER_TILES_MOBILE,
      { autoTrack: true },
    )

    const { data: allCarts } = useQueryAllShoppingCarts()
    const cartCountByLocationId = allCarts?.reduce<Record<number, number>>(
      (acc, { cart, context }) => {
        if (cart.num_items > 0) acc[context.store_location_id] = cart.num_items
        return acc
      },
      {},
    )

    const { trackCMSElementClicked } = useCMSTrackingEvents()
    const header = useHeaderRect()
    const handleViewAll = () => {
      trackCMSElementClicked({
        link_name: 'view_all_stores',
        type: 'link',
      })
      const bottomStores = document.getElementById('bottom-stores-carousel')
      if (bottomStores) {
        // subtract out sticky header height
        const position = bottomStores.getBoundingClientRect().top - header.height
        window.scrollTo({ top: position, behavior: 'smooth' })
      } else {
        logWarn('Stores Carousel View All behavior not implemented')
      }
    }

    const getCMSAttributeTracking = (store: StoreCardsRenderData) => {
      const isNew = store.recently_launched
      const hasCartInProgress = !!cartCountByLocationId?.[store.store_location_id]
      // Badges are mutually exclusive in the UI (New takes priority over cart),
      // so the visible `attributes` array shows the cart badge only when not new.
      const showCartBadge = !isNew && hasCartInProgress
      const visibleBadgeTags = [
        ...(isNew ? ['new'] : []),
        ...(showCartBadge ? ['cart_in_progress'] : []),
      ]
      // `available_attributes` captures both flags for measurement.
      const availableBadgeTags = [
        ...(isNew ? ['new'] : []),
        ...(hasCartInProgress ? ['cart_in_progress'] : []),
      ]
      const firstPrimaryAttributeText = store.primary_attributes?.[0]?.data.text
      const combinedAttributesText = [
        ...(store.primary_attributes ?? []),
        ...(store.secondary_attributes ?? []),
      ].map((attributes) => attributes.data.text)

      const promoIds = store.primary_attributes?.flatMap(
        (attribute) =>
          attribute.metadata?.read_operation_info.source_object.object.promotion_id ?? [],
      )

      return {
        promos: promoIds,
        attributes: [
          ...visibleBadgeTags,
          ...(firstPrimaryAttributeText ? [firstPrimaryAttributeText] : []),
        ],
        availableAttributes: [...availableBadgeTags, ...combinedAttributesText],
      }
    }

    const handleStoreClicked = (store: StoreCardsRenderData, index: number) => {
      selectStore({
        ...store,
        id: store.retailer_id,
        is_pickup: !!store.pickup_eligible,
      })
      trackAvailableStoreClicked({
        storeId: store.retailer_id,
        storeName: store.name,
        index,
        displaySublocation: analyticSlug ?? 'store_list_carousel',
        ...getCMSAttributeTracking(store),
      })
    }

    const onInView = (store: StoreCardsRenderData, index: number) => {
      trackAvailableStoreViewed({
        storeId: store.retailer_id,
        storeName: store.name,
        index,
        displaySublocation: analyticSlug ?? 'store_list_carousel',
        ...getCMSAttributeTracking(store),
      })
    }

    const renderStoreTile = (store: StoreCardsRenderData, storeIndex: number) => (
      <ScrollIntoView onInView={() => onInView(store, storeIndex)} key={store.retailer_id}>
        <StoreButton
          href={getRetailerUrl(store.name)}
          onClick={(e) => {
            e.preventDefault()
            handleStoreClicked(store, storeIndex)
          }}
          aria-label={`click to select ${store.name}`}
        >
          <StoreTile
            image={store.logo.src}
            name={store.name}
            backgroundColor={store.background_color}
            showNewBadge={store.recently_launched}
            attributes={store.primary_attributes}
            cartItemCount={cartCountByLocationId?.[store.store_location_id]}
          />
        </StoreButton>
      </ScrollIntoView>
    )

    const headerProps: StoresCarouselHeaderProps = {
      heading,
      subheading,
      seoHeadLevel,
      view_all_link,
      handleViewAll,
      image,
      mobile_image,
      disclaimer,
      carousel_title,
    }

    const desktopHeaderAndCarousel = (
      <>
        <DesktopHeader {...headerProps} />
        <CarouselSection.Carousel>
          {deliveryStoresList.map(renderStoreTile)}
        </CarouselSection.Carousel>
      </>
    )

    const DesktopCarousel = (
      <CarouselSection className="stores-carousel-desktop" hasInlineControls>
        {wrapDesktopInCard ? (
          <CarouselSection.Card className="stores-carousel-card">
            {desktopHeaderAndCarousel}
          </CarouselSection.Card>
        ) : (
          desktopHeaderAndCarousel
        )}
      </CarouselSection>
    )

    const MobileCarousel = (
      <CarouselSection hasInlineControls className="stores-carousel-mobile">
        <MobileHeader {...headerProps} />
        <CarouselSection.Carousel
          layoutConfig={
            smallerTilesOnMobile && deliveryStoresList.length >= 9
              ? {
                  // @ts-expect-error valid option but types aren't coming thru correctly
                  0: {
                    slidesPerView: 3.2,
                    slidesPerGroup: 3,
                  },
                  480: {
                    slidesPerView: 3.5,
                    slidesPerGroup: 3,
                  },
                  768: {
                    slidesPerView: 2.5,
                    slidesPerGroup: 1,
                  },
                }
              : undefined
          }
        >
          {deliveryStoresList.length > 5
            ? chunk(deliveryStoresList, 2).map((tuple, tupleIndex) => (
                <Tuple key={tuple[0]?.retailer_id}>
                  {tuple.map((store, storeIndex) =>
                    renderStoreTile(store, tupleIndex * 2 + storeIndex),
                  )}
                </Tuple>
              ))
            : deliveryStoresList.map(renderStoreTile)}
        </CarouselSection.Carousel>
      </CarouselSection>
    )

    return (
      <Wrapper
        {...getCMSWrapperProps({ content_type_id, id })}
        background_color_hex={background_color_hex}
        data-has-bg={Boolean(background_color_hex)}
      >
        {/* wrapped with ClientOnly since the carousel uses swiper internally and the
            layout logic relies on window object, which is not available during SSR. */}
        <ClientOnly fallback={<Skeleton width="100%" height={196} />}>
          {isMobile ? MobileCarousel : DesktopCarousel}
        </ClientOnly>
        <BotOnly>
          <h2>{heading ?? 'Stores near you'}</h2>
          {!!subheading && <h3>{subheading}</h3>}
          <ul>
            {deliveryStoresList.map((store) => (
              <li key={`${store.store_location_id}-${store.retailer_id}`}>
                <a href={getRetailerUrl(store.name)} aria-label={store.name}>
                  {store.name}
                </a>
              </li>
            ))}
          </ul>
        </BotOnly>
      </Wrapper>
    )
  }

  return StoresCarousel
}

const Tuple = styled.div`
  display: grid;
  flex-grow: 1;
  gap: ${spacingPx('sm')};
  grid-template-rows: 1fr 1fr;
`

const StoreButton = styled(TransparentAnchor)`
  flex-grow: 1;
  align-self: start;
  text-align: left;
  width: 100%;

  &:focus-visible {
    outline: solid 2px ${theme.color.focus.default};
    border-radius: 12px;
  }
`
